import React, { forwardRef, PropsWithRef } from 'react';
import classnames from 'classnames';
import { Icon, IconProps } from '../icon';

import { Size } from '../types';
import styles from './icon-button.module.scss';

export type IconButtonProps = PropsWithRef<
  {
    iconName: IconProps['name'];
    ariaLabel: string;
    variant: 'primary' | 'secondary' | 'ghost' | 'secondary-on-media' | 'primary-brabus' | 'primary-lime';
    size?: Size;
    label?: string;
  } & Pick<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    | 'id'
    | 'type'
    | 'disabled'
    | 'onClick'
    | 'aria-labelledby'
    | 'aria-expanded'
    | 'aria-controls'
    | 'autoFocus'
    | 'onContextMenu'
    | 'className'
  >
>;

const baseClass = 'icon-button';

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      label,
      'aria-labelledby': ariaLabelledBy,
      'aria-expanded': ariaExpanded,
      'aria-controls': ariaControls,
      iconName,
      ariaLabel,
      disabled,
      variant = 'primary',
      size = '300',
      className,
      onClick,
      type,
      autoFocus,
      onContextMenu
    },
    ref
  ) => {
    const classes = classnames(
      styles[baseClass],
      styles[`${baseClass}--${variant}`],
      styles[`${baseClass}--${size}`],
      className
    );

    const Button = () => (
      <button
        ref={ref}
        data-testid="icon-button-component"
        className={classes}
        aria-label={ariaLabel}
        disabled={disabled}
        type={type}
        onClick={onClick}
        aria-labelledby={ariaLabelledBy}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        autoFocus={autoFocus}
        onContextMenu={onContextMenu}
      >
        <Icon name={iconName} className={styles[`${baseClass}__icon`]} />
      </button>
    );

    if (label) {
      return (
        <div className={styles[`${baseClass}-container`]}>
          <Button />
          <span className={styles[`${baseClass}__label`]} id={ariaLabelledBy}>
            {label}
          </span>
        </div>
      );
    } else {
      return <Button />;
    }
  }
);

IconButton.displayName = 'IconButton';
